<template>
  <div class="url-resource-viewer">
    <!-- Main Content Area -->
    <div
      v-if="!dismissedReader"
      class="content-area"
    >
      <button
        type="button"
        class="dismiss-button"
        title="Do not show this message again"
        @click="dismissReaderMessage"
      >
        <i class="pi pi-times" />
        Do not show
      </button>
      
      <div class="message-container">
        <i class="pi pi-globe" />
        <h3>Web Resource</h3>
        <div class="info-box">
          <i class="pi pi-info-circle" />
          <p>
            <strong>Summaries</strong>, <strong>comprehension questions</strong>, and <strong>vocabulary extraction</strong> are available for this resource.
          </p>
        </div>
        <p class="description">
          Since this is a web resource, please open the page in your browser to view the full content and use the Chrome Extension to look up vocabulary.
        </p>
        
        <a 
          v-if="sourceUrl"
          :href="sourceUrl" 
          target="_blank" 
          rel="noopener noreferrer"
          class="open-button"
        >
          <i class="pi pi-external-link" />
          Open in Browser
        </a>

        <div class="extension-section">
          <div class="extension-content">
            <div class="extension-wrapper extension-description">
              <p class="extension-message">
                Enhance your learning with our Chrome Extension. 
                Highlight unfamiliar words directly on any webpage and instantly save them to your vocabulary list for review.
              </p>

              <a 
                :href="chromeExtensionUrl"
                target="_blank" 
                rel="noopener noreferrer"
                class="extension-button"
                aria-label="Get Chrome Extension"
                title="Get Chrome Extension"
              >
                <img
                  src="/media/chrome_store.png"
                  alt="Get Chrome Extension"
                  class="extension-image"
                >
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { CHROME_EXTENSION_URL } from '@/assets/constants/constants';

/**
 * URLResourceViewer - Displays URL resources without rendering page content
 * Provides link to open in browser while allowing summary/comprehension questions
 * to be generated and displayed via the parent ResourceReader component
 */
export default {
  name: 'WebViewer',
  props: {
    sourceUrl: {
      type: String,
      required: true
    },
    activeSentenceText: {
      type: String,
      default: null
    }
  },
  emits: ['document-loaded', 'hide-url-reader'],
  data() {
    return {
      chromeExtensionUrl: CHROME_EXTENSION_URL,
      dismissedReader: false
    };
  },
  mounted() {
    // Emit document-loaded to indicate this viewer is ready
    // This allows summary/comprehension question processing to begin
    this.$emit('document-loaded');
  },
  methods: {
    dismissReaderMessage() {
      this.dismissedReader = true;
      this.$emit('hide-url-reader');
    }
  }
};
</script>

<style scoped>
.url-resource-viewer {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
}

/* Source URL Container */
.source-url-container {
  background-color: #f5f5f5;
  padding: 12px 20px;
  border-bottom: 1px solid #ddd;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 8px;
}

.source-label {
  color: #666;
  font-size: 13px;
  font-weight: 500;
  flex-shrink: 0;
}

.source-url {
  color: #667eea;
  text-decoration: none;
  font-size: 13px;
  word-break: break-all;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.source-url:hover {
  text-decoration: underline;
  color: #764ba2;
}

/* Content Area */
.content-area {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  background-color: #fafafa;
  overflow-y: auto;
  position: relative;
}

.message-container {
  text-align: center;
  max-width: 780px;
}

.message-container i {
  font-size: 48px;
  color: #667eea;
  margin-bottom: 16px;
  display: block;
}

.message-container h3 {
  font-size: 24px;
  color: #333;
  margin: 0 0 12px 0;
  font-weight: 600;
}

.description {
  font-size: 15px;
  color: #666;
  margin: 0 0 24px 0;
  line-height: 1.6;
}

.open-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background-color: #667eea;
  color: white;
  text-decoration: none;
  border-radius: 6px;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.2s ease;
  margin-bottom: 24px;
}

.open-button:hover {
  background-color: #764ba2;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.open-button i {
  font-size: 14px;
}

.info-box {
  background-color: #e7f3ff;
  border: 1px solid #b3d9ff;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 20px;
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.info-box i {
  color: #0066cc;
  font-size: 16px;
  flex-shrink: 0;
  margin-top: 2px;
}

.info-box p {
  color: #0066cc;
  font-size: 13px;
  line-height: 1.5;
  margin: 0;
}

.info-box strong {
  font-weight: 600;
}

/* Chrome Extension Box */
.extension-section {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #ddd;
}

.extension-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.extension-wrapper {
  flex: 1;
  background: linear-gradient(135deg, #e8ecff 0%, #f0e6ff 100%);
  border-left: 4px solid #0066cc;
  padding: 16px;
  border-radius: 6px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 50px;
  box-shadow: #999 0px 4px 12px;
}

.extension-message {
  color: #333;
  font-size: 15px;
  line-height: 1.7;
  margin: 0;
  font-weight: 500;
  margin-right: 20px;
}

.extension-description {
  font-size: 15px;
  color: #333;
  margin: 0;
  font-weight: 500;
  font: italic 15px/1.7 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.extension-box {
  flex-shrink: 0;
  display: flex;
  justify-content: center;
}

.extension-button {
  display: inline-block;
  text-decoration: none;
  border: none;
  cursor: pointer;
  background: none;
  padding: 0;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.extension-button:hover {
  transform: translateY(-2px);
}

.extension-image {
  width: 240px;
  height: auto;
  display: block;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
  border-radius: 6px;
  transition: box-shadow 0.2s ease;
}

.extension-button:hover .extension-image {
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.3);
}

/* Dismiss Button */
.dismiss-button {
  position: absolute;
  top: 16px;
  right: 16px;
  padding: 8px 12px;
  background-color: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 13px;
  color: #666;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.dismiss-button i {
  font-size: 14px;
}

.dismiss-button:hover {
  background-color: #efefef;
  border-color: #999;
  color: #333;
}

.dismiss-button:active {
  transform: scale(0.98);
}
</style>
